import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../../context/authContext";
import { Alert, Box, Button, Divider, Typography } from "@mui/material";
import PageTitle from "../../../components/PageTitle/PageTitle";
import ExpandableDataGrid from "../../../components/ExpandableDataGrid/ExpandableDataGrid";
import { AddCircleOutline } from "@mui/icons-material";
import "../../Services/Services.css";
import { useTranslation } from "react-i18next";
import NotAuthorized from "../../../components/NotAuthorized/NotAuthorized";
import CreateCaseForm from "../../../components/ContactCCRForm/CreateCaseForm";
import { formatDate } from "../../../utilities/globalServiceUtils";
import { useCasesData } from "../../../hooks/useServiceRecords";
import DataGridSkeleton from "../../../components/PageSkeletons/DataGridSkeleton";

const Cases = () => {
    const { hasPermission } = useAuth();
    const { t } = useTranslation();
    const [createCaseOpen, setCreateCaseOpen] = useState(false);
    const { cases, casesLoading, refreshCases } = useCasesData();
    const navigate = useNavigate();
    const location = useLocation();

    // ?open_case=<case_number> deep link (CHIP's get_service_cases_by_site) — auto-expands
    // the matching case row once real case data has loaded. Mirrors the ?open_bom=1/
    // ?open_model=1 pattern in Products.js: gate on a ref that tracks a REAL fetch having
    // started (not just casesLoading being false), since useAsyncData's `loading` state
    // starts `false` for one render before its own effect flips it to `true` — reading the
    // query param on that very first render would search an still-empty `cases` array and
    // mark the deep link "handled" before the real data ever arrives (the same same-render
    // stale-state race Products.js's own comments describe).
    const casesFetchStartedRef = useRef(false);
    useEffect(() => {
        if (casesLoading) casesFetchStartedRef.current = true;
    }, [casesLoading]);

    const openCaseHandledForRef = useRef(null);
    const [autoExpandCaseId, setAutoExpandCaseId] = useState(null);
    useEffect(() => {
        if (!casesFetchStartedRef.current || casesLoading) return;
        const params = new URLSearchParams(location.search);
        const openCase = params.get("open_case");
        if (!openCase) return;
        if (openCaseHandledForRef.current === openCase) return;
        openCaseHandledForRef.current = openCase;

        const normalizedTarget = openCase.trim().toLowerCase();
        const match = (cases || []).find(
            (c) => String(c?.case_number || "").trim().toLowerCase() === normalizedTarget
        );
        if (match) setAutoExpandCaseId(match.id);

        // Strip the query param so it can't re-fire on a later re-render or resurrect on
        // back-navigation, same as Products.js's ?open_bom=1/?open_model=1 handlers.
        params.delete("open_case");
        const newSearch = params.toString();
        navigate(`${location.pathname}${newSearch ? `?${newSearch}` : ""}`, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search, casesLoading, cases]);

    // ?filter_case_type=/?filter_case_status=/?filter_case_priority=/?filter_date_from=/
    // ?filter_date_to= deep-link filters (CHIP's get_service_cases_by_site) — mirrors
    // WorkOrders.js's identical ?filter_wo_type=/etc. handling exactly (same rationale: the
    // chat answer was already correct, but the page itself kept showing the full unfiltered
    // list since nothing here ever read these params). case_type/case_status/case_priority go
    // through ExpandableDataGrid's initialColumnFilters (the SAME state its own filter-icon UI
    // reads/writes); CHIP already resolved these to real, exact column label values
    // server-side (comma-separated). created_date has no column-filter equivalent on this
    // page, so it's applied as a direct pre-filter on the data passed to the grid instead.
    const filterStripPendingRef = useRef(false);
    const [initialColumnFilters, setInitialColumnFilters] = useState(null);
    const [dateRangeFilter, setDateRangeFilter] = useState(null);

    // Applies filters from a raw "?filter_case_type=...&..." search string. Factored out of
    // the location-based effect below so it can ALSO be driven directly by the "chip:navigate"
    // event (see below) — live-reported bug: "show me closed cases" then "show me all cases"
    // left the Closed filter stuck active, because the second navigate's destination
    // ("/service/cases", no query string) was IDENTICAL to what the first request's own
    // strip-navigate had already left the URL at. React Router's location didn't reliably
    // retrigger this effect for that same-string case — the exact same "same-route no-op"
    // failure class already documented on ChatBot.js's `refresh_request_list` handler, just
    // never hit here until a filter needed to be cleared back to nothing. Confirmed via the
    // backend: get_service_cases_by_site DID return a correct `agent_actions: navigate to
    // /service/cases` for "show me all cases" — this was purely a frontend delivery gap.
    const applyCaseFiltersFromSearch = (searchString) => {
        const params = new URLSearchParams(searchString || "");
        const filterKeys = ["filter_case_type", "filter_case_status", "filter_case_priority", "filter_date_from", "filter_date_to"];

        // Always set state from THIS request's params — including clearing back to {}/null when
        // no filter params are present — so a new chat-driven request fully REPLACES the
        // previous one instead of ANDing with a stale filter (see WorkOrders.js for the
        // live-reproduced bug this avoids).
        const columnFilters = {};
        if (params.get("filter_case_type")) columnFilters.case_type = params.get("filter_case_type").split(",").filter(Boolean);
        if (params.get("filter_case_status")) columnFilters.case_status = params.get("filter_case_status").split(",").filter(Boolean);
        if (params.get("filter_case_priority")) columnFilters.case_priority = params.get("filter_case_priority").split(",").filter(Boolean);
        setInitialColumnFilters(columnFilters);

        const dateFrom = params.get("filter_date_from");
        const dateTo = params.get("filter_date_to");
        setDateRangeFilter(dateFrom || dateTo ? { from: dateFrom || null, to: dateTo || null } : null);

        return filterKeys.some((k) => params.get(k));
    };

    useEffect(() => {
        if (filterStripPendingRef.current) {
            filterStripPendingRef.current = false;
            return;
        }
        const hadAnyFilterParam = applyCaseFiltersFromSearch(location.search);
        if (hadAnyFilterParam) {
            const params = new URLSearchParams(location.search);
            ["filter_case_type", "filter_case_status", "filter_case_priority", "filter_date_from", "filter_date_to"].forEach((k) => params.delete(k));
            const newSearch = params.toString();
            filterStripPendingRef.current = true;
            navigate(`${location.pathname}${newSearch ? `?${newSearch}` : ""}`, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search, location.key]);

    // Fires on EVERY chat-driven navigate to this page and is the PRIMARY filter-application
    // path — not just for the "clear back to bare" case. Originally scoped to only the empty-
    // search case (reasoning: a genuinely different non-empty search string should always
    // change location.search and fire the effect above reliably) — live-reproduced wrong on
    // the analogous Work Orders page: "installation work orders" -> "work orders for machine
    // X" (two DIFFERENT non-empty filter query strings) ALSO failed to apply via the location
    // effect alone. So this event is now unconditional whenever `dest` targets this page;
    // applyCaseFiltersFromSearch is idempotent (just recomputes state from a given search
    // string), so it being ALSO called again by the location effect once the deferred
    // navigate() actually lands is harmless redundancy, not a conflict. ChatBot.js dispatches
    // this synchronously with the real destination path BEFORE its own deferred navigate()
    // call, so `detail.path`'s query string is authoritative and doesn't depend on
    // location-diffing timing/reliability at all.
    useEffect(() => {
        const onChipNavigate = (e) => {
            const dest = String(e?.detail?.path || "");
            if (!dest.startsWith("/service/cases")) return;
            const qIndex = dest.indexOf("?");
            const searchString = qIndex >= 0 ? dest.slice(qIndex) : "";
            applyCaseFiltersFromSearch(searchString);
            // useCasesData's fetch is keyed ONLY on selectedSite (useServiceRecords.js), so a
            // chat-driven re-ask for the SAME site never retried the data itself — only the
            // filters above. If the underlying fetch ever failed/came back stale (e.g. a
            // startup timing race before site context settled), the page was stuck showing
            // that stale state until selectedSite genuinely changed or a manual reload forced
            // a fresh fetch. Force a real refetch on every chat-driven arrival here too.
            refreshCases();
        };
        window.addEventListener("chip:navigate", onChipNavigate);
        return () => window.removeEventListener("chip:navigate", onChipNavigate);
        // refreshCases IS a real dep (it's rebuilt whenever selectedSite changes) — leaving it
        // out would let this listener close over a stale site's refetch function.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshCases]);

    // Dedicated "chip:navigate" handler for ?open_case= specifically (mirrors WorkOrders.js's
    // onChipNavigateOpenWo, which this page never had). The location-based effect above only
    // reliably resolves ?open_case= on a genuine location.search change — a same-route re-ask
    // for a DIFFERENT case while already on this page can be a "same-route no-op" React Router
    // doesn't reconcile via location alone (the identical gap the filter listener above already
    // works around for filter_* params). ChatBot.js dispatches this event synchronously with
    // the real destination path BEFORE its own deferred navigate() call, so detail.path's query
    // string is authoritative here too, independent of location-diffing timing.
    useEffect(() => {
        const onChipNavigateOpenCase = (e) => {
            const dest = String(e?.detail?.path || "");
            if (!dest.startsWith("/service/cases")) return;
            if (casesLoading) return;
            const qIndex = dest.indexOf("?");
            const searchString = qIndex >= 0 ? dest.slice(qIndex) : "";
            const openCase = new URLSearchParams(searchString).get("open_case");
            if (!openCase) {
                setAutoExpandCaseId(null);
                return;
            }
            const normalizedTarget = openCase.trim().toLowerCase();
            const match = (cases || []).find(
                (c) => String(c?.case_number || "").trim().toLowerCase() === normalizedTarget
            );
            setAutoExpandCaseId(match ? match.id : null);
        };
        window.addEventListener("chip:navigate", onChipNavigateOpenCase);
        return () => window.removeEventListener("chip:navigate", onChipNavigateOpenCase);
    }, [casesLoading, cases]);

    const displayedCases = useMemo(() => {
        if (!dateRangeFilter) return cases;
        const from = dateRangeFilter.from ? new Date(dateRangeFilter.from) : null;
        const to = dateRangeFilter.to ? new Date(dateRangeFilter.to) : null;
        if (to) to.setHours(23, 59, 59, 999);
        return (cases || []).filter((c) => {
            if (!c?.created_date) return false;
            const d = new Date(c.created_date);
            if (Number.isNaN(d.getTime())) return false;
            if (from && d < from) return false;
            if (to && d > to) return false;
            return true;
        });
    }, [cases, dateRangeFilter]);

    const columns_cases = [
        { field: "case_number", headerName: t('TABLE_HEADERS.CASE_NUMBER'), width: 150 },
        { field: "case_title", headerName: t('TABLE_HEADERS.TITLE'), flex: 1.5 },
        { field: "case_type", headerName: t('TABLE_HEADERS.TYPE'), width: 150, filterable: true },
        { field: "case_status", headerName: t('TABLE_HEADERS.STATUS'), width: 140, filterable: true },
        { field: "case_priority", headerName: t('TABLE_HEADERS.PRIORITY'), width: 140, filterable: true },
        {
            field: "created_date", headerName: t('TABLE_HEADERS.CREATED_DATE'), width: 200, valueFormatter: (value) => formatDate(value),
            // Ensure proper date sorting even if value is a string
            sortComparator: (v1, v2) => {
                const d1 = v1 ? new Date(v1).getTime() : 0;
                const d2 = v2 ? new Date(v2).getTime() : 0;
                return d1 - d2;
            },
        },
        {
            field: "resolution_date", headerName: t('TABLE_HEADERS.CLOSED_DATE'), width: 200, valueFormatter: (value) => formatDate(value),
            // Ensure proper date sorting even if value is a string
            sortComparator: (v1, v2) => {
                const d1 = v1 ? new Date(v1).getTime() : 0;
                const d2 = v2 ? new Date(v2).getTime() : 0;
                return d1 - d2;
            },
        },
    ];

    const handleCreateCase = () => {
        setCreateCaseOpen(true);
    };

    const customButtonsCases = [
        {
            label: t('LABELS.CREATE_NEW_CASE'),
            onClick: () => {
                handleCreateCase();
            },
            icon: <AddCircleOutline />,
        },
    ];

    // Removed Create New Workorder button

    const canSeeCases = !!hasPermission('service_management.view_cases');
    const canCreateCase = !!hasPermission('service_management.create_cases');

    //   If user can't see either tab, block access entirely
    if (!canSeeCases) {
        return <NotAuthorized />;
    }

    if (casesLoading) {
        return (
            <DataGridSkeleton
                containerClassName="services-container"
                actionButtons={canCreateCase ? 1 : 0}
                columns={5}
                rows={6}
            />
        );
    }




    // Ensure valid tab index when permissions change dynamically

    const renderCaseMasterDetails = (caseRow, detailActions) => (
        <Box sx={{
            p: 2, width: '100%', border: "1px solid #e6e8eb",
            borderRadius: 2,
        }}>
            <Box sx={{ mb: 2 }}>
                <Typography variant="body1" fontWeight={"bold"}>Case Details: {caseRow?.case_number}</Typography>
                <Typography variant="body2" color="text.secondary">Please review the case details below</Typography>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "flex-start", alignItems: "center", columnGap: 2 }}>
                <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2">{t("TABLE_HEADERS.CASE_NUMBER")}</Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>{caseRow?.case_number}</Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2">{t("TABLE_HEADERS.TYPE")}</Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>{caseRow?.case_type}</Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2">{t("TABLE_HEADERS.STATUS")}</Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>{caseRow?.case_status}</Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2">{t("TABLE_HEADERS.PRIORITY")}</Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>{caseRow?.case_priority}</Typography>
                </Box>
            </Box>

            <Divider sx={{ my: 2 }} flexItem />
            <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2">{t("TABLE_HEADERS.TITLE")}</Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>{caseRow?.case_title}</Typography>
            </Box>
            <Divider sx={{ my: 2 }} flexItem />
            <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2">{t("TABLE_HEADERS.DESCRIPTIONS")}</Typography>
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", mb: 1 }}>{caseRow?.case_description || "-"}</Typography>
            </Box>
            <Divider sx={{ my: 2 }} flexItem />
            <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2">{t("TABLE_HEADERS.RESOLUTION_DETAILS")}</Typography>
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", mb: 1 }}>{caseRow?.case_resolution_details || "-"}</Typography>
            </Box>
            <Divider sx={{ my: 2 }} flexItem />
            <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2">{t("TABLE_HEADERS.RESOLUTION_NOTES", { defaultValue: "Resolution Notes" })}</Typography>
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>{caseRow?.case_resolution_notes || "-"}</Typography>
            </Box>
            <Divider sx={{ my: 2 }} flexItem />
            <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "flex-start", alignItems: "center", columnGap: 2 }}>
                <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2">{t("TABLE_HEADERS.CREATED_DATE")}</Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>{formatDate(caseRow?.created_date)}</Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2">{t("TABLE_HEADERS.RESOLUTION_DATE")}</Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>{formatDate(caseRow?.resolution_date) || "-"}</Typography>
                </Box>
            </Box>
            <Divider sx={{ my: 2 }} flexItem />
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Button variant="outlined" onClick={detailActions?.collapseRow}>
                    {t("BUTTON.CLOSE")}
                </Button>
            </Box>
        </Box>
    );

    return (
        <div className="services-container">
            <PageTitle title={t('TITLES.CASES_TICKETS')} />


            {cases.length === 0 && !casesLoading ? (
                <Alert severity="info" className="error-alert" sx={{ marginTop: "10px" }}>
                    {t('ALERT_MESSAGES.NO_CASES_FOUND')}
                </Alert>
            ) : (
                <ExpandableDataGrid
                    key="casesGrid"
                    data={displayedCases}
                    columns={columns_cases}
                    pageSize={5}
                    showSearchBar={true}
                    checkboxSelection={false}
                    customButtons={canCreateCase ? customButtonsCases : []}
                    rowIdField="id"
                    sortBy={[{ field: "created_date", sort: "desc" }]}
                    masterDetails={renderCaseMasterDetails}
                    allowMultipleExpand={false}
                    initialExpandedId={autoExpandCaseId}
                    initialColumnFilters={initialColumnFilters}
                />
            )}


            {/* Create Case Modal */}
            <CreateCaseForm open={createCaseOpen} setOpen={setCreateCaseOpen} />


        </div>
    );

};

export default Cases;
